import { useRef, useState } from 'react';

import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { KeyedMutator } from 'swr';

import { UserRoleEnum } from '~enums';
import { useSession } from '~hooks';
import useAuthorize from '~hooks/useAuthorize';
import { OffsetPagedResults } from '~interfaces';
import User from '~interfaces/user';
import { getService } from '~services/serviceFactory';

import UsersActionBar from '../components/usersActionBar';
import UsersDataGrid from '../dataGrids/usersDataGrid';
import { EditUserFormResults } from '../forms/editUserForm';
import UsersService from '../services/usersService';

interface Props {
	showOrganisationFilter: boolean;
	showOrganisationColumn: boolean;
	showSuperAdminRole: boolean;
}

type UsersAction = 'none' | 'invite';

const usersService = getService(UsersService);

const UsersOverviewContainer = (props: Props) => {
	const { authorize } = useAuthorize();
	const { t } = useTranslation();
	const { enqueueSnackbar } = useSnackbar();
	const { user } = useSession();
	const [isInvitingUser, setIsInvitingUser] = useState<boolean>(false);
	const [userAction, setUserAction] = useState<UsersAction>('none');
	const isClientAdmin = authorize([UserRoleEnum.ClientAdmin]);
	const mutateRef = useRef<KeyedMutator<OffsetPagedResults<User>> | null>(null);

	const updateMutate = (mutate: KeyedMutator<OffsetPagedResults<User>>) => {
		mutateRef.current = mutate;
	};

	const clearUserAction = () => {
		setUserAction('none');
	};

	const inviteUser = (formData: EditUserFormResults) => {
		const organisationId = user?.organisationId;
		if (!organisationId) {
			enqueueSnackbar(t('user.messages.inviteUserFailure', { ns: 'entities' }), {
				variant: 'error',
			});
			return;
		}

		setIsInvitingUser(true);
		usersService
			.inviteUser({ userData: formData, organisationId: user?.organisationId })
			.then((_) => {
				enqueueSnackbar(t('user.messages.inviteUserSuccess', { ns: 'entities' }), {
					variant: 'success',
				});

				mutateRef?.current?.();
				clearUserAction();
			})
			.catch((_error) => {
				enqueueSnackbar(t('user.messages.inviteUserFailure', { ns: 'entities' }), {
					variant: 'error',
				});
			})
			.finally(() => {
				setIsInvitingUser(false);
			});
	};

	return (
		<>
			<UsersActionBar
				showInviteUserButton={isClientAdmin}
				showInviteUserForm={userAction === 'invite'}
				isInvitingUser={isInvitingUser}
				onInviteUser={inviteUser}
				onInviteUserButtonClicked={() => {
					setUserAction('invite');
				}}
				onCancel={clearUserAction}
			/>
			<UsersDataGrid
				showOrganisationFilter={props.showOrganisationFilter}
				showOrganisationColumn={props.showOrganisationColumn}
				onMutateUpdated={updateMutate}
			/>
		</>
	);
};

export default UsersOverviewContainer;
